/**
 * Outbox IndexedDB layer — FWA-PWA-029.
 *
 * Single-store schema con `idb` (Jake Archibald). PII tier Crítico:
 * clearAll() obligatorio en logout y 401 handlers.
 */
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import { ulid } from 'ulid';
import type { OutboxMethod, OutboxRecord, OutboxStatus } from './types.js';

export const DB_NAME = 'sisrep-outbox-v1';
export const DB_VERSION = 1;
export const STORE_NAME = 'outbox';

interface OutboxDBSchema extends DBSchema {
  outbox: {
    key: string;
    value: OutboxRecord;
    indexes: {
      by_status: OutboxStatus;
      by_created_at: string;
      by_next_retry_at: string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OutboxDBSchema>> | null = null;

function getDB(): Promise<IDBPDatabase<OutboxDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OutboxDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by_status', 'status');
          store.createIndex('by_created_at', 'created_at');
          store.createIndex('by_next_retry_at', 'next_retry_at');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Reset interno — para tests que necesitan limpiar entre cases.
 * No usar en código de producción.
 */
export function _resetDbPromiseForTesting(): void {
  dbPromise = null;
}

export interface AddOutboxItemInput {
  endpoint: string;
  method: OutboxMethod;
  payload: unknown;
  headers?: Record<string, string>;
}

export async function addOutboxItem(input: AddOutboxItemInput): Promise<OutboxRecord> {
  const db = await getDB();
  const now = new Date().toISOString();
  const record: OutboxRecord = {
    id: ulid(),
    endpoint: input.endpoint,
    method: input.method,
    payload: input.payload,
    headers: input.headers ?? {},
    status: 'pending',
    attempts: 0,
    last_error: null,
    created_at: now,
    updated_at: now,
    next_retry_at: null,
    conflict: false,
  };
  await db.put(STORE_NAME, record);
  return record;
}

export async function listOutbox(filter?: { status?: OutboxStatus }): Promise<OutboxRecord[]> {
  const db = await getDB();
  if (filter?.status) {
    return db.getAllFromIndex(STORE_NAME, 'by_status', filter.status);
  }
  return db.getAll(STORE_NAME);
}

export async function getOutboxById(id: string): Promise<OutboxRecord | undefined> {
  const db = await getDB();
  return db.get(STORE_NAME, id);
}

export async function updateOutboxItem(
  id: string,
  patch: Partial<OutboxRecord>,
): Promise<OutboxRecord | undefined> {
  const db = await getDB();
  const current = await db.get(STORE_NAME, id);
  if (!current) return undefined;
  const updated: OutboxRecord = {
    ...current,
    ...patch,
    updated_at: new Date().toISOString(),
  };
  await db.put(STORE_NAME, updated);
  return updated;
}

export async function deleteOutboxItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

/**
 * Limpia TODOS los registros — invocar on logout o on 401.
 * PII tier Crítico mitigation per privacy.md §6.
 */
export async function clearAll(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAME);
}

/**
 * Devuelve items elegibles para sync inmediato.
 * Criterio: status='pending' o (status='failed' transiente con next_retry_at <= now).
 */
export async function findItemsReadyForSync(nowIso: string): Promise<OutboxRecord[]> {
  const all = await listOutbox();
  return all
    .filter((r) => r.status === 'pending')
    .filter((r) => {
      if (!r.next_retry_at) return true;
      return r.next_retry_at <= nowIso;
    })
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function countByStatus(): Promise<Record<OutboxStatus, number>> {
  const all = await listOutbox();
  const counts: Record<OutboxStatus, number> = {
    pending: 0,
    syncing: 0,
    success: 0,
    failed: 0,
  };
  for (const r of all) {
    counts[r.status] = (counts[r.status] ?? 0) + 1;
  }
  return counts;
}
